import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HelpCircle } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { HubMockupBadge } from "@/components/HubMockupBadge";
import { fetchHubMockupEffective } from "@/lib/hubMockupApi";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import {
  formatNumber,
  mrpPlanningOrderLinesCacheKey,
  MRP_PLANNING_TLA_BOM_VARIABLE_NAME,
  type MrpPlanningOrderLinesQuarterlyResponse,
  type MrpPlanningQuarterlyLineRow,
} from "@/lib/mrp";

const PLANNING_TLA_COL_CLASS =
  "sticky left-0 z-10 w-[1%] whitespace-nowrap bg-white px-3 py-2 after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-slate-200";

const PAST_QUARTER_OPTIONS = [3, 4, 5, 8, 12, 16] as const;

const PLANNING_BOM_MESSAGE_PREVIEW = 260;

const PLANNING_PAGE_HOVER_HINT =
  "Non-cancelled Megaventory sales order lines from public.sales_order_item, grouped by calendar quarter. TLA (FI-*-TLA-* assembly) comes from the BoM topLevel slot via process_boms at depth 1 — the same shallow explosion as forecast BoM parsing. Distinct finished-good SKUs are exploded once per batch with per-SKU fallback when the batch fails; resolver errors skip only those SKUs and those lines aggregate under Unknown. Optional mrp.assembly labels come from the resolved component_sku through mrp.assembly_sku. See docs/bom-system-complete.md rule priority 2.00. Configure hub.mockup with JSON like {\"mrp\": {\"pages\": {\"planning\": true}}} when you want the hub to forbid MRP mutations while leaving this reporting page available.";

const QUARTERLY_REPORT_HOVER_HINT =
  "Rows are TLAs (BoM topLevel component_sku). Columns are calendar quarters. Total sums quantity across the window; Average is total divided by the number of quarters shown.";

const PLANNING_SUMMARY_COL_CLASS =
  "whitespace-nowrap border-l border-slate-200 bg-slate-50/80 px-4 py-2 text-right tabular-nums font-medium text-slate-900";

type PlanningPivotRow = {
  key: string;
  label: string;
  sublabel: string | null;
  isUnknown: boolean;
  quantitiesByQuarter: Map<string, number>;
};

function buildPlanningPivot(rows: MrpPlanningQuarterlyLineRow[]): {
  quarterLabels: string[];
  pivotRows: PlanningPivotRow[];
} {
  const quarterOrder: { label: string; firstDay: string }[] = [];
  const quarterSeen = new Set<string>();
  for (const row of rows) {
    if (!quarterSeen.has(row.quarter_label)) {
      quarterSeen.add(row.quarter_label);
      quarterOrder.push({ label: row.quarter_label, firstDay: row.quarter_first_day });
    }
  }
  quarterOrder.sort((left, right) => left.firstDay.localeCompare(right.firstDay));

  const rowMap = new Map<string, PlanningPivotRow>();
  for (const row of rows) {
    const isUnknown = row.bom_component_sku == null && row.assembly_code == null;
    const key = isUnknown
      ? "__unknown__"
      : (row.bom_component_sku ?? row.assembly_code ?? "__unknown__").trim();
    const label = isUnknown
      ? "Unknown"
      : row.assembly_code != null && row.assembly_code.trim() !== ""
        ? row.assembly_code.trim()
        : (row.bom_component_sku ?? "Unknown").trim();

    let pivotRow = rowMap.get(key);
    if (!pivotRow) {
      const sublabel =
        !isUnknown &&
        row.bom_component_sku != null &&
        row.assembly_code != null &&
        row.assembly_code.trim() !== "" &&
        row.bom_component_sku.trim() !== row.assembly_code.trim()
          ? row.bom_component_sku.trim()
          : row.assembly_display_name != null && String(row.assembly_display_name).trim() !== "" &&
              String(row.assembly_display_name).trim() !== label
            ? String(row.assembly_display_name).trim()
            : null;

      pivotRow = {
        key,
        label,
        sublabel,
        isUnknown,
        quantitiesByQuarter: new Map(),
      };
      rowMap.set(key, pivotRow);
    }

    const existing = pivotRow.quantitiesByQuarter.get(row.quarter_label) ?? 0;
    pivotRow.quantitiesByQuarter.set(row.quarter_label, existing + row.quantity_sum);
  }

  const pivotRows = [...rowMap.values()].sort((left, right) => {
    if (left.isUnknown && !right.isUnknown) return 1;
    if (!left.isUnknown && right.isUnknown) return -1;
    return left.label.localeCompare(right.label, undefined, { sensitivity: "base", numeric: true });
  });

  return { quarterLabels: quarterOrder.map((quarter) => quarter.label), pivotRows };
}

function pivotRowTotal(pivotRow: PlanningPivotRow, quarterLabels: string[]): number {
  let sum = 0;
  for (const quarterLabel of quarterLabels) {
    sum += pivotRow.quantitiesByQuarter.get(quarterLabel) ?? 0;
  }
  return sum;
}

function pivotRowAverage(total: number, quarterCount: number): number {
  if (quarterCount <= 0) return 0;
  return total / quarterCount;
}

const FILTER_SELECT_CLASS =
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

const PLANNING_HELP_BUTTON_CLASS =
  "inline-flex shrink-0 rounded p-0.5 text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1";

function truncatePlanningBomMessage(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length <= PLANNING_BOM_MESSAGE_PREVIEW) return trimmed;
  return `${trimmed.slice(0, PLANNING_BOM_MESSAGE_PREVIEW)}…`;
}

function formatQuarterlyRangeSummary(data: MrpPlanningOrderLinesQuarterlyResponse): string {
  const parts = [
    `Slot: ${data.variable_name}`,
    `Range: ${data.from_inclusive} → exclusive ${data.to_exclusive}`,
  ];
  if (typeof data.past_quarters === "number") {
    parts.push(
      `Preset: ${data.past_quarters} past completed ${data.past_quarters === 1 ? "quarter" : "quarters"}${
        data.include_current_quarter === true ? " · including ongoing quarter" : ""
      }${data.include_current_quarter === false ? " · excluding ongoing quarter" : ""}`,
    );
  }
  return parts.join(" · ");
}

function UnknownOrderSkusHoverPanel({
  unknownOrderSkus,
  bomResolutionErrors,
}: {
  unknownOrderSkus: string[];
  bomResolutionErrors: MrpPlanningOrderLinesQuarterlyResponse["bom_resolution_errors"];
}): JSX.Element {
  if (unknownOrderSkus.length === 0) {
    return (
      <p className="max-w-xs text-slate-700">
        No sales order line SKUs in this window rolled up to Unknown. If you still see Unknown quantities, refresh the
        page after the API reloads.
      </p>
    );
  }

  return (
    <div className="max-w-sm space-y-2">
      <p className="font-medium text-slate-800">
        Sales order SKUs with no resolved {MRP_PLANNING_TLA_BOM_VARIABLE_NAME} TLA ({unknownOrderSkus.length})
      </p>
      <ul className="max-h-64 list-none space-y-1 overflow-y-auto p-0 m-0 font-mono text-[11px] leading-snug text-slate-800">
        {unknownOrderSkus.map((sku) => (
          <li key={sku}>{sku}</li>
        ))}
      </ul>
      {bomResolutionErrors.length > 0 ? (
        <div className="space-y-1 border-t border-slate-200 pt-2 text-xs text-slate-700">
          <p className="font-medium text-slate-800">BoM resolver errors</p>
          <ul className="list-none space-y-1 p-0 m-0">
            {bomResolutionErrors.map((bomErr) => (
              <li key={bomErr.sku}>
                <span className="font-mono">{bomErr.sku}</span>
                <span> — {truncatePlanningBomMessage(bomErr.message)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function Planning() {
  const queryClient = useQueryClient();
  const [pastQuarters, setPastQuarters] = useState<number>(5);
  const [includeCurrentQuarter, setIncludeCurrentQuarter] = useState<boolean>(false);

  const hubPlanning = useQuery({
    queryKey: ["hub-mockup", "mrp", "planning"],
    queryFn: () => fetchHubMockupEffective("mrp", "planning"),
    staleTime: 60_000,
  });

  const quarterlyQueryKey = [
    "mrp",
    "planning",
    "order-lines-by-quarter",
    { past_quarters: pastQuarters, include_current_quarter: includeCurrentQuarter },
  ] as const;
  const planningHubCacheChannel = HUB_CACHE_CHANNEL.mrpPlanningOrderLines(
    mrpPlanningOrderLinesCacheKey({
      pastQuarters,
      includeCurrentQuarter,
    }),
  );

  const quarterlyQuery = useQuery({
    queryKey: quarterlyQueryKey,
    queryFn: () =>
      apiFetch<MrpPlanningOrderLinesQuarterlyResponse>("/api/mrp/planning/order-lines-by-quarter", {
        query: {
          past_quarters: pastQuarters,
          ...(includeCurrentQuarter ? { include_current_quarter: true } : {}),
          background: "1",
        },
      }),
    staleTime: 2 * 60_000,
    refetchOnWindowFocus: false,
  });

  useHubCacheStream({
    channels: [planningHubCacheChannel],
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: quarterlyQueryKey });
      }
    },
  });

  const writesBlockedFlag = hubPlanning.data?.writesBlocked === true;
  const pageHoverHint = writesBlockedFlag
    ? `${PLANNING_PAGE_HOVER_HINT} Hub mock-up (workflow.app_settings hub.mockup) is blocking write routes for module mrp; this screen is read-only.`
    : PLANNING_PAGE_HOVER_HINT;

  const bomResolutionErrors = quarterlyQuery.data?.bom_resolution_errors ?? [];
  const pivot = useMemo(
    () => buildPlanningPivot(quarterlyQuery.data?.rows ?? []),
    [quarterlyQuery.data?.rows],
  );
  const tlaRowCount = pivot.pivotRows.length;
  const hasPivotData = (quarterlyQuery.data?.rows.length ?? 0) > 0;
  const pivotColumnCount = pivot.quarterLabels.length + 3;
  const unknownOrderSkus = quarterlyQuery.data?.unknown_order_skus ?? [];

  return (
    <div className="space-y-4">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <PageTitleWithHelp hoverHint={pageHoverHint}>Planning</PageTitleWithHelp>
          {hubPlanning.data?.bannerMockupVisible ? <HubMockupBadge /> : null}
        </div>
      </header>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 pt-0 md:grid-cols-2">
          <div>
            <Label htmlFor="past-quarters">Rolling window</Label>
            <select
              id="past-quarters"
              aria-label="Number of completed calendar quarters strictly before today’s UTC quarter"
              className={`mt-1 ${FILTER_SELECT_CLASS}`}
              value={pastQuarters}
              onChange={(event) => {
                setPastQuarters(Number.parseInt(event.target.value, 10));
              }}
            >
              {PAST_QUARTER_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  Past {n} completed quarters (excluding current quarter)
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col justify-end gap-1.5">
            <label
              htmlFor="include-current-quarter"
              className="flex cursor-pointer items-start gap-2 text-sm font-medium text-slate-700"
            >
              <input
                id="include-current-quarter"
                type="checkbox"
                className="relative top-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={includeCurrentQuarter}
                onChange={(event) => {
                  setIncludeCurrentQuarter(event.target.checked);
                }}
              />
              Include current quarter
            </label>
            <p className="text-xs text-slate-500">
              When checked, adds the ongoing UTC calendar quarter ({pastQuarters} past + current = {pastQuarters + 1}{" "}
              quarters in total).
            </p>
          </div>
          {quarterlyQuery.data ? (
            <p className="text-xs text-slate-500 md:col-span-2">{formatQuarterlyRangeSummary(quarterlyQuery.data)}</p>
          ) : null}
        </CardBody>
      </Card>

      {bomResolutionErrors.length > 0 ? (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm ring-1 ring-amber-200/70"
        >
          <p className="font-medium">
            The BoM resolver skipped {bomResolutionErrors.length} finished-good SKU
            {bomResolutionErrors.length === 1 ? "" : "s"} during process_boms (those order lines aggregate under Unknown
            below).
          </p>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 marker:text-amber-800">
            {bomResolutionErrors.map((bomErr) => (
              <li key={bomErr.sku}>
                <span className="font-mono text-slate-900">{bomErr.sku}</span>
                <span className="text-slate-700"> — {truncatePlanningBomMessage(bomErr.message)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <CardTitleWithHelp hoverHint={QUARTERLY_REPORT_HOVER_HINT}>
              Sales lines by quarter (BoM slot {MRP_PLANNING_TLA_BOM_VARIABLE_NAME})
            </CardTitleWithHelp>
            {quarterlyQuery.data && !quarterlyQuery.isLoading && !quarterlyQuery.error ? (
              <p className="text-xs text-slate-500">
                {tlaRowCount} {tlaRowCount === 1 ? "TLA" : "TLAs"} × {pivot.quarterLabels.length}{" "}
                {pivot.quarterLabels.length === 1 ? "quarter" : "quarters"}
              </p>
            ) : null}
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className={`${PLANNING_TLA_COL_CLASS} bg-slate-50`}>TLA (BoM component)</th>
                  {pivot.quarterLabels.map((quarterLabel) => (
                    <th key={quarterLabel} className="whitespace-nowrap px-4 py-2 text-right">
                      {quarterLabel}
                    </th>
                  ))}
                  <th className={`${PLANNING_SUMMARY_COL_CLASS} bg-slate-50`}>Total</th>
                  <th className="whitespace-nowrap px-4 py-2 text-right">Average</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {quarterlyQuery.isLoading ? (
                  <tr>
                    <td
                      colSpan={Math.max(3, pivotColumnCount)}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : quarterlyQuery.error ? (
                  <tr>
                    <td
                      colSpan={Math.max(3, pivotColumnCount)}
                      className="px-4 py-6 text-red-700"
                    >
                      {(quarterlyQuery.error as Error).message ??
                        String((quarterlyQuery.error as { message?: unknown })?.message)}
                    </td>
                  </tr>
                ) : !hasPivotData ? (
                  <tr>
                    <td
                      colSpan={Math.max(3, pivotColumnCount)}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No qualifying sales lines for this span.
                    </td>
                  </tr>
                ) : (
                  pivot.pivotRows.map((pivotRow) => {
                    const total = pivotRowTotal(pivotRow, pivot.quarterLabels);
                    const average = pivotRowAverage(total, pivot.quarterLabels.length);
                    return (
                    <tr key={pivotRow.key} className="group hover:bg-slate-50/70">
                      <td className={`${PLANNING_TLA_COL_CLASS} relative group-hover:bg-slate-50/70`}>
                        {pivotRow.isUnknown ? (
                          <div className="flex items-center gap-1.5">
                            <span className="italic text-slate-500">Unknown</span>
                            <HoverHintTrigger
                              trigger={
                                <button
                                  type="button"
                                  className={PLANNING_HELP_BUTTON_CLASS}
                                  aria-label={`Sales order SKUs with no resolved ${MRP_PLANNING_TLA_BOM_VARIABLE_NAME} TLA`}
                                >
                                  <HelpCircle className="h-4 w-4" />
                                </button>
                              }
                            >
                              <UnknownOrderSkusHoverPanel
                                unknownOrderSkus={unknownOrderSkus}
                                bomResolutionErrors={bomResolutionErrors}
                              />
                            </HoverHintTrigger>
                          </div>
                        ) : (
                          <div className="space-y-0.5">
                            <span className="font-mono text-brand-800">{pivotRow.label}</span>
                            {pivotRow.sublabel != null ? (
                              <div className="text-xs text-slate-500">{pivotRow.sublabel}</div>
                            ) : null}
                          </div>
                        )}
                      </td>
                      {pivot.quarterLabels.map((quarterLabel) => {
                        const quantity = pivotRow.quantitiesByQuarter.get(quarterLabel);
                        return (
                          <td
                            key={quarterLabel}
                            className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-800"
                          >
                            {quantity != null && quantity !== 0 ? formatNumber(quantity) : "—"}
                          </td>
                        );
                      })}
                      <td className={PLANNING_SUMMARY_COL_CLASS}>{formatNumber(total)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-800">
                        {formatNumber(average)}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
